/**
 * Transport system: loads land units into naval transports and disgorges them
 * back onto shore. Runs AFTER production (so a just-trained ship can be a target)
 * and BEFORE combat/movement (so boarded/unloaded units settle this tick).
 *
 * Garrison capacity is measured in POPULATION points: a unit aboard keeps its
 * ident/owner/health (still counts as alive) but loses its transform/move, so
 * every transform-iterating system skips it. Population was counted at spawn and
 * is NOT re-counted on board/unload — only a SUNK transport decrements it (via
 * world.destroy's cascade).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import type { Eid } from "../types";
import { findPath, nearestWalkableHex } from "../pathfinding";
import { boardingShore } from "../naval";
import { repathToDoor } from "../orders";

/** Population points currently aboard a carrier. */
function used(world: World, occupants: Eid[]): number {
  let n = 0;
  for (const occ of occupants) n += world.ident.get(occ)?.population ?? 1;
  return n;
}

/** Eject a STATIONARY carrier's garrison (a tower) onto walkable land around it,
 *  clustered toward (towardX,towardY). Unlike a ship — which sails to the drop
 *  point first (handled in the system below) — a tower can't move, so this places
 *  the occupants immediately. Returns true if anyone disembarked. */
export function ejectGarrison(world: World, reg: Registry, carrier: Eid, towardX: number, towardY: number): boolean {
  const gar = world.garrison.get(carrier);
  const tr = world.transform.get(carrier);
  if (!gar || !tr || gar.occupants.length === 0) return false;
  const home = world.hexAtWorld(tr.pos.x, tr.pos.y);
  if (!home) return false;
  // Everyone pours out the SAME door — the one walkable tile beside the tower
  // nearest the click — then they all WALK together to the clicked rally point.
  // (Splitting them across several adjacent tiles left some standing at the rally
  // while others trickled in — the inconsistent spread the player saw.)
  const ring = world.neighbors(home.col, home.row).filter(([c, r]) => world.passableFor("land", c, r));
  if (ring.length === 0) return false;
  ring.sort((a, b) => {
    const ca = world.hexCenter(a[0], a[1]), cb = world.hexCenter(b[0], b[1]);
    const da = (ca.x - towardX) ** 2 + (ca.y - towardY) ** 2;
    const db = (cb.x - towardX) ** 2 + (cb.y - towardY) ** 2;
    return da - db || a[0] - b[0] || a[1] - b[1];
  });
  const occ = [...gar.occupants].sort((a, b) => a - b); // deterministic order
  const [hc, hr] = ring[0]; // the single door, on the click-facing side
  const c = world.hexCenter(hc, hr);
  const tile = Math.sqrt(3) * world.size;
  occ.forEach((u, i) => {
    const idn = world.ident.get(u);
    if (!idn?.civ) return;
    const def = reg.unit(idn.civ, idn.defId);
    // a small golden-angle fan so coincident bodies separate cleanly as they leave
    const ang = (i * 2.39996) % (Math.PI * 2);
    const rr = world.size * 0.16 * (1 + (i % 3));
    const pos = { x: c.x + Math.cos(ang) * rr, y: c.y + Math.sin(ang) * rr };
    world.transform.set(u, { pos, radius: (def.radius ?? 0.18) * world.size, facing: 0 });
    // pour OUT the door, then WALK to the tile the player clicked — a stationary
    // tower can't carry them there, so they march to the drop point as one group.
    const owner = world.owner.get(u)?.player;
    const path = findPath(world, pos.x, pos.y, towardX, towardY, 6000, "land", owner, false, world.transform.get(u)?.radius ?? world.size * 0.18) ?? [];
    world.move.set(u, { speed: def.speed * tile, path, queue: [], intent: { kind: "move", x: towardX, y: towardY } });
  });
  gar.occupants.length = 0;
  return true;
}

/** Up to `n` LAND hexes a disembarking unit can actually stand on: a climb-aware
 *  flood-fill from the shore seed (col,row), only crossing edges where the unit
 *  could WALK (passable + within MAX_CLIMB) — so cargo never lands on a cliff
 *  ledge it couldn't have climbed to. The connected region is then sorted nearest
 *  the clicked drop point. Deterministic (distance, then col, then row). */
function landingHexes(world: World, col: number, row: number, n: number, fromX: number, fromY: number): [number, number][] {
  const canLand = (c: number, r: number) =>
    world.passableFor("land", c, r) && !world.solidHexKeys.has(world.hkey(c, r));
  // seed must be walkable land; if the shore hex itself isn't, there's nowhere to land.
  if (!canLand(col, row)) return [];
  const seen = new Set<number>([world.hkey(col, row)]);
  const found: { c: number; r: number; d: number }[] = [];
  const cap = n * 4 + 12; // explore a little past what we need, then pick the nearest
  const stack: [number, number][] = [[col, row]];
  while (stack.length && found.length < cap) {
    const [cc, cr] = stack.pop()!;
    const ctr = world.hexCenter(cc, cr);
    found.push({ c: cc, r: cr, d: (ctr.x - fromX) ** 2 + (ctr.y - fromY) ** 2 });
    for (const [nc, nr] of world.neighbors(cc, cr)) {
      const k = world.hkey(nc, nr);
      if (seen.has(k)) continue;
      // only step where the unit could actually walk (no water/building, no cliff)
      if (!canLand(nc, nr) || !world.canClimb(cc, cr, nc, nr)) continue;
      seen.add(k); stack.push([nc, nr]);
    }
  }
  found.sort((a, b) => a.d - b.d || a.c - b.c || a.r - b.r);
  return found.slice(0, Math.max(1, n)).map((f) => [f.c, f.r] as [number, number]);
}

export function transportSystem(world: World, reg: Registry): void {
  const tile = Math.sqrt(3) * world.size;

  for (const [eid, mv] of world.move) {
    const intent = mv.intent;
    if (!intent) continue;

    // ---- a land unit boarding a transport ----
    if (intent.kind === "embark") {
      const tr = world.transform.get(eid);
      const gar = world.garrison.get(intent.transport);
      const tTr = world.transform.get(intent.transport);
      if (!tr || !gar || !tTr) { mv.intent = undefined; continue; } // ship gone → cancel
      const d = Math.hypot(tTr.pos.x - tr.pos.x, tTr.pos.y - tr.pos.y);
      const isBuilding = world.building.has(intent.transport);
      // Garrison range is MODEL TOUCH: a tower takes you in the instant your body
      // reaches its model (tower radius + your radius + a hair), NOT from a tile out.
      // A ship is the exception — it sits in water, so you board from the adjacent
      // shore across the unavoidable waterline gap (~1 tile).
      const reach = isBuilding ? tTr.radius + tr.radius + world.size * 0.15 : world.size * 1.95;
      if (d <= reach) {
        const pop = world.ident.get(eid)?.population ?? 1;
        if (used(world, gar.occupants) + pop <= gar.capacity) {
          gar.occupants.push(eid);
          world.transform.delete(eid); // aboard: no position, no movement
          world.move.delete(eid);
        } else {
          mv.intent = undefined; // no room — give up gracefully (stays on shore)
        }
      } else if (mv.path.length === 0) {
        // not in range and not moving: close the gap. For a TOWER, walk right up to
        // its model edge (model touch). For a SHIP, walk to the shore on our own side;
        // if we've already reached it and the hull is still out in open water, WAIT
        // (re-pathing our own hex would soft-lock) — board when it's brought closer.
        if (isBuilding) {
          repathToDoor(world, eid, intent.transport);
        } else {
          const sc = boardingShore(world, intent.transport, tr.pos.x, tr.pos.y);
          if (sc && Math.hypot(sc.x - tr.pos.x, sc.y - tr.pos.y) > world.size * 0.5) {
            mv.path = findPath(world, tr.pos.x, tr.pos.y, sc.x, sc.y, 6000, "land", world.owner.get(eid)?.player, false, tr.radius) ?? [];
          }
        }
      }
      continue;
    }

    // ---- a transport disgorging its cargo onto land ----
    if (intent.kind === "unload") {
      const gar = world.garrison.get(eid);
      const tr = world.transform.get(eid);
      if (!gar || !tr) { mv.intent = undefined; continue; }
      if (gar.occupants.length === 0) { mv.intent = undefined; continue; }
      if (mv.path.length > 0) continue; // still sailing toward the drop point
      // Disembark ON the tile the player CLICKED (the ship has sailed to the coast
      // nearest it). Seed the landing flood at the clicked hex when it's walkable
      // land, else at the nearest walkable land to the ship in the click's
      // direction; then pick hexes nearest the CLICK — so the squad lands where the
      // player pointed, not just wherever the hull happens to sit. Never crashes
      // when the ship is stranded in open water with no land in reach.
      const clickHex = world.hexAtWorld(intent.x, intent.y);
      // Honour the clicked tile when the ship is actually NEAR it (it sailed there);
      // a click far from where the hull ended up (unreachable / different landmass)
      // falls back to landing beside the ship rather than teleporting the squad.
      const nearClick = !!clickHex && Math.hypot(intent.x - tr.pos.x, intent.y - tr.pos.y) <= world.size * 7;
      let seed: [number, number] | null =
        nearClick && world.passableFor("land", clickHex!.col, clickHex!.row) && !world.solidHexKeys.has(world.hkey(clickHex!.col, clickHex!.row)) ? [clickHex!.col, clickHex!.row] : null;
      if (!seed) {
        const sh = world.hexAtWorld(tr.pos.x, tr.pos.y);
        const toward = nearClick ? { x: intent.x, y: intent.y } : { x: tr.pos.x, y: tr.pos.y };
        seed = sh ? nearestWalkableHex(world, sh.col, sh.row, 8, toward.x, toward.y, "land") : null;
      }
      if (!seed) { mv.intent = undefined; continue; } // open sea, nowhere to land → keep cargo aboard
      const occ = [...gar.occupants].sort((a, b) => a - b); // deterministic order
      // Cluster the disembark TIGHTLY on/around the clicked tile: ~3 units per hex
      // over only the closest few tiles (separation fans them within a tile),
      // instead of fanning a small squad across many tiles.
      const want = Math.max(1, Math.ceil(occ.length / 3));
      // rank landing hexes nearest the CLICK when it's the target, else nearest the ship
      const rx = nearClick ? intent.x : tr.pos.x, ry = nearClick ? intent.y : tr.pos.y;
      const hexes = landingHexes(world, seed[0], seed[1], want, rx, ry);
      if (hexes.length === 0) { mv.intent = undefined; continue; } // guard: never index an empty set
      occ.forEach((u, i) => {
        const idn = world.ident.get(u);
        if (!idn?.civ) return;
        const def = reg.unit(idn.civ, idn.defId);
        const [hc, hr] = hexes[i % hexes.length];
        const c = world.hexCenter(hc, hr);
        // tight golden-angle micro-scatter so coincident units separate cleanly
        const ang = (i * 2.39996) % (Math.PI * 2);
        const rr = world.size * 0.14 * (i % 3);
        world.transform.set(u, {
          pos: { x: c.x + Math.cos(ang) * rr, y: c.y + Math.sin(ang) * rr },
          radius: (def.radius ?? 0.18) * world.size,
          facing: 0,
        });
        world.move.set(u, { speed: def.speed * tile, path: [], queue: [] });
      });
      gar.occupants.length = 0;
      mv.intent = undefined;
      continue;
    }
  }
}
